/**
 * Notifications Service Utilities
 *
 * Helper functions for notification operations
 */

import type { Notification } from './types';

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format notification date
 */
export function formatNotificationDate(date: string): string {
  const notifDate = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - notifDate.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 1 hour ago
  if (diffMins < 60) {
    if (diffMins === 0) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  }

  // Less than 24 hours ago
  if (diffHours < 24) {
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  }

  // Less than 7 days ago
  if (diffDays < 7) {
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  }

  // Format as date
  return notifDate.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: notifDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

/**
 * Get notification type label
 */
export function getNotificationTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    task_overdue: 'Task Overdue',
    task_assigned: 'Task Assigned',
    task_completed: 'Task Completed',
    communication_received: 'Communication Received',
    communication_sent: 'Communication Sent',
    document_processed: 'Document Processed',
    document_uploaded: 'Document Uploaded',
    matter_update: 'Matter Update',
    status_change: 'Status Change',
    system: 'System'
  };

  return (
    typeLabels[type] ||
    type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

/**
 * Get notification icon based on type
 */
export function getNotificationIcon(type: string): string {
  const typeIcons: Record<string, string> = {
    task_overdue: 'AlertCircle',
    task_assigned: 'UserPlus',
    task_completed: 'CheckCircle',
    communication_received: 'MessageSquare',
    communication_sent: 'Send',
    document_processed: 'FileCheck',
    document_uploaded: 'Upload',
    matter_update: 'FileText',
    status_change: 'RefreshCw',
    system: 'Info'
  };

  return typeIcons[type] || 'Bell';
}

// ============================================================================
// Filtering Functions
// ============================================================================

/**
 * Filter notifications by status
 */
export function filterNotificationsByStatus(
  notifications: Notification[],
  status: 'read' | 'unread'
): Notification[] {
  return notifications.filter((n) => n.status === status);
}

// ============================================================================
// Search Functions
// ============================================================================

/**
 * Search notifications
 */
export function searchNotifications(
  notifications: Notification[],
  query: string
): Notification[] {
  const lowerQuery = query.toLowerCase();

  return notifications.filter(
    (notification) =>
      (notification.title?.toLowerCase() || '').includes(lowerQuery) ||
      notification.message.toLowerCase().includes(lowerQuery) ||
      notification.type.toLowerCase().includes(lowerQuery)
  );
}

// ============================================================================
// Cache Key Functions
// ============================================================================

/**
 * Get cache key for notifications
 */
export function getNotificationsCacheKey(): string[] {
  return ['notifications'];
}

/**
 * Get cache key for unread count
 */
export function getUnreadCountCacheKey(): string[] {
  return ['notifications', 'unread-count'];
}

/**
 * Get cache key for matters with unread
 */
export function getMattersWithUnreadCacheKey(): string[] {
  return ['notifications', 'matters-with-unread'];
}
